import Link from 'next/link';
import { useTranslations } from 'next-intl';

import { Image } from '@/components/shared/Image';
import { P, H1 } from '@/components/shared/Typography';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';
import { C_Routes } from '@/ts/constants/C_Routes';
import { E_Routes } from '@/ts/enums/E_Routes';

type T_ErrorType = 'not-found' | 'internal';

interface I_ErrorLayoutProps {
  type: T_ErrorType;
  imagePath: string;
  className?: string;
}

const ErrorLayout = ({ type, imagePath, className }: I_ErrorLayoutProps) => {
  const t = useTranslations('errors');

  // Get title and description based on error type
  const getErrorContent = () => {
    switch (type) {
      case 'not-found':
        return {
          title: t('pageNotFoundTitle'),
          description: t('pageNotFoundMessage')
        };
      case 'internal':
        return {
          title: t('internalErrorTitle'),
          description: t('internalErrorMessage')
        };
      default:
        return {
          title: t('generic'),
          description: t('generic')
        };
    }
  };

  const { title, description } = getErrorContent();

  return (
    <div
      className={cn(
        'flex h-main relative z-1 px-8 flex-col items-center justify-center py-11 md:bg-[linear-gradient(90deg,hsla(0,75%,15%,1)_0%,hsla(0,0%,0%,1)_100%)]',
        className
      )}
    >
      <div className="max-w-(--container-width) w-full mx-auto flex flex-col md:flex-row items-center justify-between gap-2">
        <div className="max-w-114 w-full">
          <H1 className="font-extrabold">
            {type === 'not-found' && (
              <>
                {t('oops')}
                <br />
              </>
            )}
            {title}
          </H1>
          <P className="mt-4 mb-7 text-lg">{description}</P>
          {type === 'not-found' && (
            <Button
              className="w-full border border-outline-border h-10"
              asChild
            >
              <Link href={C_Routes[E_Routes.HOME].link}>
                {t('pageNotFoundAction')}
              </Link>
            </Button>
          )}
        </div>
        <div className="w-full max-w-116 relative h-83">
          <Image src={imagePath} alt="Something went wrong on our end" />
        </div>
      </div>
    </div>
  );
};

export default ErrorLayout;
